import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { roleLabel } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Trash2, Loader2, Shield, User } from "lucide-react";

export default function Users() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ name: "", email: "", password: "", role: "sales_executive" });

  const load = () => api.get("/users").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.email || !draft.password) return toast.error("Email and password required");
    try {
      await api.post("/users", draft);
      toast.success("User created");
      setOpen(false);
      setDraft({ name: "", email: "", password: "", role: "sales_executive" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create user"); }
  };

  const patch = async (u, body) => {
    try { await api.patch(`/users/${u.id}`, body); load(); }
    catch (e) { toast.error("Could not update"); }
  };

  const remove = async (u) => {
    try { await api.delete(`/users/${u.id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  return (
    <div className="pgt-fade-up space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C9A34A] font-semibold">Access control</div>
          <h1 className="font-display text-4xl text-[#0B3B2E]">Users & Permissions</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0B3B2E] hover:bg-[#14513D] gap-2" data-testid="user-add"><UserPlus className="h-4 w-4" /> Add User</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-display text-2xl text-[#0B3B2E]">Create User</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Full name</Label><Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} data-testid="user-name" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={draft.email} onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))} data-testid="user-email" /></div>
              <div className="space-y-1.5"><Label>Password</Label><Input type="text" value={draft.password} onChange={(e) => setDraft((d) => ({ ...d, password: e.target.value }))} data-testid="user-password" /></div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={draft.role} onValueChange={(v) => setDraft((d) => ({ ...d, role: v }))}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales_executive">Sales Executive</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={create} className="bg-[#0B3B2E] hover:bg-[#14513D]" data-testid="user-create">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {rows === null ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#0B3B2E]" /></div>
      ) : (
        <div className="space-y-3">
          {rows.map((u) => (
            <Card key={u.id} data-testid={`user-row-${u.email}`} className="p-4 flex flex-wrap items-center gap-4 border-[#C9A34A]/25">
              <div className={`grid h-10 w-10 place-items-center rounded-full ${u.role === "admin" ? "bg-[#0B3B2E] text-[#C9A34A]" : "bg-[#C9A34A]/20 text-[#0B3B2E]"}`}>
                {u.role === "admin" ? <Shield className="h-5 w-5" /> : <User className="h-5 w-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[#0B3B2E]">{u.name || u.email}</div>
                <div className="text-xs text-[#14513D]/60">{u.email}</div>
              </div>
              <Select value={u.role} onValueChange={(v) => patch(u, { role: v })} disabled={u.id === user.id}>
                <SelectTrigger className="w-40" data-testid={`role-${u.email}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales_executive">Sales Executive</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={u.is_active} onCheckedChange={(v) => patch(u, { is_active: v })} disabled={u.id === user.id} data-testid={`active-${u.email}`} />
                {u.is_active ? "Active" : "Disabled"}
              </label>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(u)} disabled={u.id === user.id} data-testid={`del-user-${u.email}`}><Trash2 className="h-4 w-4" /></Button>
            </Card>
          ))}
        </div>
      )}

      <Card className="p-5 bg-[#FAF7F0] border-[#C9A34A]/25">
        <div className="font-semibold text-[#0B3B2E] mb-2">Role permissions</div>
        <div className="grid sm:grid-cols-2 gap-4 text-sm text-[#14513D]/80">
          <div>
            <Badge className="bg-[#0B3B2E] text-[#C9A34A] mb-2">Administrator</Badge>
            <p>Manage users, brand & Tea Board licence, Google rating, marketplace logos, catalogue-number prefix, product library, and all catalogues.</p>
          </div>
          <div>
            <Badge variant="outline" className="mb-2 border-[#C9A34A] text-[#0B3B2E]">Sales Executive</Badge>
            <p>Create/edit own catalogues, upload covers, add products & reports, preview, export and share. Cannot change licence, official logos or number rules.</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
